<template>
  <v-app>
    <v-app-bar dense max-height="96">
      <img
        src="/static/logo2.png"
        style="max-width: 100px"
        class="mt-12 ml-8"
      />
      <v-spacer />

      <div v-if="currentDocument.RequiresValidation">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              class="mt-12"
              color="green"
              elevation="10"
              fab
              x-large
              @click="validate"
            >
              <v-icon color="white">mdi-check-bold</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Validate") }}</span>
        </v-tooltip>
      </div>
      <div v-if="currentDocument.RequiresValidation">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              class="mt-12"
              color="error"
              elevation="10"
              fab
              x-large
              @click="reject"
            >
              <v-icon color="white">mdi-close-thick</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Refuse") }}</span>
        </v-tooltip>
      </div>
      <div v-if="currentDocument.RequiresSignature">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              class="mt-12"
              color="green"
              elevation="10"
              fab
              x-large
              @click="sign"
            >
              <v-icon color="white">mdi-draw</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Sign") }}</span>
        </v-tooltip>
      </div>
      <template v-slot:extension>
        <v-tabs
          centered
          style="
            padding-left: 120px !important;
            padding-right: 120px !important;
          "
        >
          <v-tab
            v-for="(doc, i) in documentList"
            :key="doc.objectID"
            @click="loadDocument(doc)"
          >
            <v-icon large class="mr-1">mdi-file-document-outline</v-icon>
            #{{ i + 1 }}
          </v-tab>
        </v-tabs>
      </template>
    </v-app-bar>
    <v-content>
      <div>
        <v-row justify="center" align="center" v-if="loading">
          <div class="text-center">
            <div>
              <v-progress-circular
                class="my-4"
                :size="50"
                width="5"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
            <p class="display-1">{{ $t("Loading") }}...</p>
          </div>
        </v-row>
        <v-row justify="center" align="center" no-gutters v-if="hasAttachments">
          <v-col cols="4">
            <v-select
              clearable
              :label="$t('DocumentAttachments')"
              :items="currentDocument.Attachments"
              @change="loadAttachment"
              item-text="Description"
              item-value="Id"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="!loading" no-gutters>
          <v-col>
            <pdf-viewer :ref="viewerKey" :height="getHeight" />
            <div v-if="currentDocument.AllowDownload" @click="download">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="mb-12"
                    v-on="on"
                    color="info"
                    elevation="10"
                    fab
                    x-large
                    bottom
                    right
                    absolute
                  >
                    <v-icon color="white">mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Download") }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-content>
    <app-core-footer />
    <v-hover v-slot="{ hover }">
      <v-snackbar
        :elevation="hover ? 16 : 2"
        :timeout="hover ? -1 : 3000"
        top
        center
        :color="snackbar.color"
        v-model="snackbar.show"
      >
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn dark v-bind="attrs" @click.native="snackbar.show = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-hover>
    <v-dialog v-model="showInfoMessage" width="60%">
      <v-card>
        <v-card-text v-html="infoMessage"></v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showCommentModal" width="60%">
      <v-card>
        <v-card-title class="pt-3">
          <span class="headline">{{ $t("Comment") }}</span>
        </v-card-title>
        <v-card-text>
          <tiptap-vuetify
            v-model="rejectComment.text"
            :extensions="extensions"
          />
        </v-card-text>
        <v-card-actions class="pt-0 pb-3 mt-3">
          <v-spacer></v-spacer>
          <v-btn
            small
            class="pa-4 text-capitalize font-weight-regular display-1"
            @click.prevent="cancelReject()"
            >{{ $t("Close") }}
          </v-btn>
          <v-btn
            color="error"
            small
            class="pa-4 text-capitalize font-weight-regular display-1"
            @click.prevent="confirmReject()"
            >{{ $t("Refuse") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import PdfViewer from "@/components/document/PdfViewer";
import BaseService from "@/ecm/common/BaseService.js";
import AppCoreFooter from "@/components/core/Footer";
import Utils from "@/ecm/common/Utils.js";
import Comment from "@/ecm/models/search/results/list/Comment";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

export default {
  components: {
    PdfViewer,
    AppCoreFooter,
    TiptapVuetify,
  },
  data() {
    return {
      distributionToken: "",
      viewerKey: "externalPdfViewer",
      documentList: [],
      currentDocument: {},
      infoMessage: "",
      rejectComment: new Comment(),
      showInfoMessage: false,
      showCommentModal: false,
      loading: false,
      snackbar: {
        show: false,
        text: "",
        color: "",
      },
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
    };
  },
  created() {
    this.distributionToken = this.$route.params.distribution_token;
    this.getDistributedDocuments();
  },
  computed: {
    viewerInstance() {
      return this.$refs[this.viewerKey];
    },
    hasAttachments() {
      return (
        this.currentDocument.Attachments &&
        this.currentDocument.Attachments.length > 0
      );
    },
    getHeight() {
      return window.innerHeight - 96 - 68 - (this.hasAttachments ? 70 : 0); //96px header, 68px footer, 24px padding, 70px attachment selector
    },
  },
  methods: {
    showErrorMessage(message) {
      this.snackbar.text = message;
      this.snackbar.show = true;
      this.snackbar.color = "red";
    },
    getDistributedDocuments() {
      this.loading = true;
      var self = this;
      BaseService.get({
        url: `external/get-distributed-documents/${this.distributionToken}`,
      })
        .then((result) => {
          self.documentList = result;
          if (self.documentList) {
            self.loadDocument(self.documentList[0]);
            self.infoMessage = self.documentList[0].InfoMessage;
            if (self.infoMessage) {
              self.showInfoMessage = true;
            }
          }
          self.loading = false;
        })
        .catch((err) => {
          self.loading = false;
          this.showErrorMessage(err.response.data.Message);
        });
    },
    validate() {
      BaseService.post({
        url: `external/validate/${this.distributionToken}/${this.currentDocument.ObjectID}`,
      })
        .then((result) => {
          this.currentDocument.RequiresValidation = false;
        })
        .catch((err) => {
          this.showErrorMessage(err.response.data.Message);
        });
    },
    reject() {
      this.rejectComment = new Comment();
      this.showCommentModal = true;
    },
    cancelReject() {
      this.rejectComment = new Comment();
      this.showCommentModal = false;
    },
    confirmReject() {
      if (!this.rejectComment.text) {
        this.showErrorMessage(this.$t("CommentRequired"));
        return;
      }
      BaseService.post({
        url: `external/reject/${this.distributionToken}/${this.currentDocument.ObjectID}`,
        data: this.rejectComment,
      })
        .then((result) => {
          this.currentDocument.RequiresValidation = false;
          this.rejectComment = new Comment();
          this.showCommentModal = false;
        })
        .catch((err) => {
          this.showErrorMessage(err.response.data.Message);
        });
    },
    sign() {
      BaseService.post({
        url: `external/sign/${this.distributionToken}/${this.currentDocument.ObjectID}`,
      })
        .then((redirectURL) => {
          window.location = redirectURL;
        })
        .catch((err) => {
          this.showErrorMessage(err.response.data.Message);
        });
    },
    download() {
      Utils.file.attachment.download(
        `external/download/${this.distributionToken}/${this.currentDocument.ObjectID}`
      );
    },
    loadDocument(document) {
      this.currentDocument = document;
      this.$nextTick(() => {
        var docURL = `${this.$store.getters["account/apiUrl"]}/api/external/display/${this.distributionToken}/${document.ObjectID}`;
        let file = encodeURIComponent(docURL);
        this.viewerInstance.load(file);
      });
    },
    loadAttachment(attID) {
      if (!attID) {
        this.loadDocument(this.currentDocument);
        return;
      }
      this.$nextTick(() => {
        var docURL = `${this.$store.getters["account/apiUrl"]}/api/external/display-attachment/${this.distributionToken}/${this.currentDocument.ObjectID}/${attID}`;
        let file = encodeURIComponent(docURL);
        this.viewerInstance.load(file);
      });
    },
  },
};
</script>
<style >
.v-toolbar__content {
  z-index: 9999 !important;
}
</style>